import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  message,
  Modal,
  Popconfirm,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Dropdown,
  Menu,
  Input,
  Form,
  Alert,
  Upload
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  SendOutlined,
  SaveOutlined,
  PrinterOutlined,
  DownloadOutlined,
  UploadOutlined,
  MailOutlined,
  CopyOutlined,
  GroupOutlined,
  DownOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import invoiceService, { Invoice, InvoiceFilters, InvoiceStats } from '../../../../services/invoiceService';
import apiService from '../../../../services/apiService';
import InvoiceBulkUploadModal from './InvoiceBulkUploadModal';
import { useTranslation } from "react-i18next";
import { findSalesJournal } from '../../utils/salesJournal';

const { Option } = Select;
const { RangePicker } = DatePicker;

interface InvoiceListProps {
  onEdit?: (invoice: Invoice) => void;
  onView?: (invoice: Invoice) => void;
  onCreate?: () => void;
  onMarkPaid?: (invoice: Invoice) => void;
  onDuplicate?: (invoice: Invoice) => void;
}

const InvoiceList: React.FC<InvoiceListProps> = ({ onEdit, onView, onCreate, onMarkPaid, onDuplicate }) => {
    const { t } = useTranslation();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<InvoiceStats | null>(null);
  const [filters, setFilters] = useState<InvoiceFilters>({});
  const [searchValue, setSearchValue] = useState<string>('');
  const [accounts, setAccounts] = useState<any[]>([]);
  const [journals, setJournals] = useState<any[]>([]);
  const [accountingSettings, setAccountingSettings] = useState<any>(null);
  const [groupBy, setGroupBy] = useState<string | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  // Files the sender adds in the dialog, on top of the generated invoice PDF.
  const [emailAttachments, setEmailAttachments] = useState<any[]>([]);
  // Bulk send progress + the per-invoice outcome we report afterwards.
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult] = useState<null | {
    sent: Array<{ invoiceNo: string; to: string }>;
    skipped: Array<{ invoiceNo: string; customer: string; reason: string }>;
    failed: Array<{ invoiceNo: string; error: string }>;
  }>(null);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [customers, setCustomers] = useState<Array<{ id: string; name: string }>>([]);
  const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const [emailModalInvoice, setEmailModalInvoice] = useState<Invoice | null>(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailForm] = Form.useForm();

  // Helper function to get group value.
  // These are display-only: they become a group header label, a `group-…` row id
  // and a sort key, and are only ever compared against themselves. Nothing here
  // is persisted, so translating them is safe. The 'status' branch deliberately
  // returns the raw persisted status untouched.
  const getGroupValue = (invoice: Invoice, groupByField: string): string => {
    switch (groupByField) {
      case 'status':
        return invoice.status || t('Unknown');
      case 'customer':
        return invoice.customer?.name || invoice.customerId || t('Unknown Customer');
      case 'dueDate':
        if (!invoice.dueDate) return t('No Due Date');
        const dueDate = dayjs(invoice.dueDate);
        const today = dayjs();
        if (dueDate.isBefore(today, 'day')) return t('Overdue');
        if (dueDate.isSame(today, 'day')) return t('Due Today');
        if (dueDate.isBefore(today.add(7, 'day'), 'day')) return t('Due This Week');
        if (dueDate.isBefore(today.add(30, 'day'), 'day')) return t('Due This Month');
        return t('Due Later');
      case 'month':
        return dayjs(invoice.invoiceDate || invoice.createdAt).format('MMMM YYYY');
      default:
        return t('Ungrouped');
    }
  };

  // Sort and group invoices with group headers
  const processedInvoices = React.useMemo(() => {
    if (!groupBy) return invoices;

    const sorted = [...invoices].sort((a, b) => {
      const groupA = getGroupValue(a, groupBy);
      const groupB = getGroupValue(b, groupBy);
      return groupA.localeCompare(groupB);
    });

    // Insert group header rows
    const withHeaders: any[] = [];
    let currentGroup = '';

    sorted.forEach((invoice) => {
      const group = getGroupValue(invoice, groupBy);
      if (group !== currentGroup) {
        currentGroup = group;
        // Add a group header row
        withHeaders.push({
          id: `group-${group}`,
          isGroupHeader: true,
          groupName: group,
          groupItems: sorted.filter(inv => getGroupValue(inv, groupBy) === group)
        });
      }
      withHeaders.push(invoice);
    });

    return withHeaders;
  }, [invoices, groupBy]);

  useEffect(() => {
    loadInvoices();
    loadStats();
    loadAccounts();
    loadJournals();
    loadCustomers();
    loadAccountingSettings();
  }, [filters]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const data = await invoiceService.getAllInvoices(filters);
      setInvoices(data);
    } catch (error: any) {
      message.error(t('Failed to load invoices: {{message}}', { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await invoiceService.getInvoiceStats();
      setStats(data);
    } catch (error: any) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadAccounts = async () => {
    try {
      const response: any = await apiService.get('/accounting/accounts');
      setAccounts(response.data || []);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const res: any = await apiService.getCustomers({ limit: 1000, isActive: true });
      const list = res?.customers || res?.data || [];
      setCustomers(list.map((c: any) => ({ id: c.id, name: c.name })));
    } catch (error) {
      console.error('Failed to load customers:', error);
    }
  };

  const loadJournals = async () => {
    try {
      const response: any = await apiService.get('/accounting/journals/active');
      setJournals(response.data || []);
    } catch (error) {
      console.error('Failed to load journals:', error);
    }
  };

  const loadAccountingSettings = async () => {
    try {
      const response: any = await apiService.get('/accounting/settings');
      setAccountingSettings(response.data || null);
    } catch (error) {
      console.error('Failed to load accounting settings:', error);
    }
  };

  // Resolve the AR / Revenue account IDs.
  // Priority: configured Default Accounts in settings → loose code-pattern fallback
  // (the fallback is intentionally loose to keep older companies working; it can pick
  // the wrong account when multiple AR-like codes exist, which is exactly why
  // configuring Default Accounts is the right path forward).
  const resolveAccountIds = () => {
    const arById = accountingSettings?.defaultReceivableAccountId
      ? accounts.find(a => a.id === accountingSettings.defaultReceivableAccountId)
      : null;
    const revenueById = accountingSettings?.defaultRevenueAccountId
      ? accounts.find(a => a.id === accountingSettings.defaultRevenueAccountId)
      : null;
    const arAccount = arById
      || accounts.find(a => a.code === '124000')
      || accounts.find(a => a.code?.startsWith('1200'));
    const revenueAccount = revenueById
      || accounts.find(a => a.code?.includes('4000') || a.code?.startsWith('41'));
    return { arAccount, revenueAccount };
  };

  const handleDelete = async (id: string) => {
    try {
      await invoiceService.deleteInvoice(id);
      message.success(t('Invoice deleted successfully'));
      loadInvoices();
      loadStats();
    } catch (error: any) {
      message.error(t('Failed to delete invoice: {{message}}', { message: error.message }));
    }
  };

  const handleStatusChange = async (invoice: Invoice, newStatus: string) => {
    try {
      if (newStatus === 'CONFIRM') {
        // Confirm = post the journal entry now (status DRAFT → CONFIRM).
        // No email sent here; Send button later only emails the customer.
        const { arAccount, revenueAccount } = resolveAccountIds();
        const salesJournal = findSalesJournal(journals);

        if (!arAccount || !revenueAccount || !salesJournal) {
          message.error(t('Missing required accounts or journal. Please ensure Accounts Receivable, Revenue, and Sales/Invoice Journal exist.'));
          return;
        }

        await invoiceService.confirmInvoice(invoice.id, {
          accountsReceivableAccountId: arAccount.id,
          revenueAccountId: revenueAccount.id,
          journalId: salesJournal.id
        });
        message.success(t('Invoice confirmed and posted to ledger'));
      } else if (newStatus === 'SENT') {
        // Prefer Default Accounts from settings, fall back to code heuristic
        const { arAccount, revenueAccount } = resolveAccountIds();
        const salesJournal = findSalesJournal(journals);  // Sales Journal

        if (!arAccount || !revenueAccount || !salesJournal) {
          message.error(t('Missing required accounts or journal. Please ensure Accounts Receivable, Revenue, and Sales/Invoice Journal exist.'));
          return;
        }

        // Post the invoice to create journal entry and change status to SENT
        await invoiceService.postInvoice(invoice.id, {
          accountsReceivableAccountId: arAccount.id,
          revenueAccountId: revenueAccount.id,
          journalId: salesJournal.id
        });
        message.success(t('Invoice posted successfully'));
      } else if (newStatus === 'PAID') {
        // Unreachable: the "Mark Paid" button calls onMarkPaid, which opens the
        // PaymentForm so the user can pick the receiving account and allocation.
        // Payment must go through that flow — it is what writes the journal entry.
        message.warning(t('Use the Mark Paid button to record a payment against this invoice.'));
        return;
      } else if (newStatus === 'OVERDUE') {
        await invoiceService.markInvoiceAsOverdue(invoice.id);
        message.success(t('Invoice marked as {{newStatus}}', { newStatus }));
      }
      loadInvoices();
      loadStats();
    } catch (error: any) {
      message.error(t('Failed to update invoice status: {{message}}', { message: error.message }));
    }
  };

  const openSendEmailModal = (invoice: Invoice) => {
    // A billing contact is somebody deliberately nominated on the customer as
    // the person invoices go to, so it beats the customer's general address.
    const billingContactEmail = (invoice.customer as any)?.billingContactEmail || '';
    const recipient =
      billingContactEmail ||
      invoice.contactEmail ||
      invoice.customer?.email ||
      '';
    const companyName = invoice.company?.name || 'WYNI ERP';
    const customerName = invoice.customer?.name || 'Customer';
    const subject = `Invoice ${invoice.invoiceNo} from ${companyName}`;
    // NOT translated on purpose. This body is sent to the *customer*, so it must
    // not follow whichever locale the operator happens to have selected.
    const body =
      `Dear ${customerName},\n\n` +
      `Please find attached invoice ${invoice.invoiceNo} for ${invoice.currency || 'HKD'} ${Number(invoice.totalAmount || 0).toFixed(2)}.\n\n` +
      `Kindly send us the payment record after processing the payment.\n\n` +
      `Thank you for your business.\n\n` +
      `${companyName}`;

    setEmailModalInvoice(invoice);
    setEmailAttachments([]);
    emailForm.setFieldsValue({
      to: recipient,
      cc: '',
      subject,
      body,
    });

    // Otherwise fall back to the addresses actually used last time for this
    // customer — the customer record often holds a generic address while
    // invoices in practice go to a named person, with accounts on CC. A billing
    // contact outranks this: it was set on purpose, whereas the history is only
    // a record of what happened to be typed before. The CC from last time is
    // still worth offering either way.
    const customerId = (invoice as any).customerId || invoice.customer?.id;
    if (customerId) {
      invoiceService
        .getEmailDefaults(customerId)
        .then((last: any) => {
          if (!last) return;
          const toStr = (last.to || []).join(', ');
          const ccStr = (last.cc || []).join(', ');
          if (!toStr && !ccStr) return;
          emailForm.setFieldsValue({
            to: billingContactEmail || toStr || recipient,
            cc: ccStr,
          });
          message.info(
            billingContactEmail
              ? `CC filled in from the last email to this customer (${dayjs(last.sentAt).format('D MMM YYYY')}). To is the billing contact.`
              : `Addresses filled in from the last email to this customer (${dayjs(last.sentAt).format('D MMM YYYY')})`
          );
        })
        .catch(() => { /* falls back to the customer record */ });
    }
  };

  const closeSendEmailModal = () => {
    setEmailModalInvoice(null);
    emailForm.resetFields();
  };

  /** Read the picked files into base64 so they can travel in the JSON body. */
  const encodeAttachments = async (files: any[]) =>
    Promise.all(
      files.map(
        (file) =>
          new Promise<{ filename: string; content: string; contentType?: string }>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = String(reader.result || '');
              resolve({
                filename: file.name,
                content: result.slice(result.indexOf(',') + 1),
                contentType: file.type || undefined,
              });
            };
            reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
            reader.readAsDataURL(file);
          })
      )
    );

  /**
   * Email every selected invoice — one message each, as its own customer's
   * invoice, matching what a single send produces.
   *
   * Roughly half the customers on file have no email address, so invoices
   * without a recipient are skipped and listed afterwards rather than being
   * allowed to fail one by one.
   */
  const handleBulkSendEmail = async () => {
    const selected = invoices.filter(
      inv => selectedRowKeys.includes(inv.id) && !(inv as any).isGroupHeader
    );
    if (selected.length === 0) return;

    // Same order of preference as the single-invoice dialog above.
    const recipientFor = (inv: any) =>
      inv.customer?.billingContactEmail || inv.contactEmail || inv.customer?.email || '';
    const sendable = selected.filter(inv => recipientFor(inv));
    const skipped = selected
      .filter(inv => !recipientFor(inv))
      .map(inv => ({
        invoiceNo: inv.invoiceNo,
        customer: inv.customer?.name || '—',
        reason: 'No email address on file',
      }));

    if (sendable.length === 0) {
      setBulkResult({ sent: [], skipped, failed: [] });
      return;
    }

    setBulkSending(true);
    const sent: Array<{ invoiceNo: string; to: string }> = [];
    const failed: Array<{ invoiceNo: string; error: string }> = [];

    // Sequential on purpose: firing dozens at the mail server at once is how
    // you get rate-limited or silently dropped.
    for (const inv of sendable) {
      const to = recipientFor(inv);
      const companyName = (inv as any).company?.name || 'WYNI ERP';
      try {
        await invoiceService.sendInvoiceEmail(inv.id, {
          to: [to],
          subject: `Invoice ${inv.invoiceNo} from ${companyName}`,
          // NOT translated on purpose — sent to the customer, not the operator.
          body:
            `Dear ${inv.customer?.name || 'Customer'},\n\n` +
            `Please find attached invoice ${inv.invoiceNo} for ${inv.currency || 'HKD'} ` +
            `${Number(inv.totalAmount || 0).toFixed(2)}.\n\n` +
            `Kindly send us the payment record after processing the payment.\n\n` +
            `Thank you for your business.\n\n${companyName}`,
        });
        sent.push({ invoiceNo: inv.invoiceNo, to });
      } catch (e: any) {
        failed.push({ invoiceNo: inv.invoiceNo, error: e?.message || 'Send failed' });
      }
    }

    setBulkSending(false);
    setBulkResult({ sent, skipped, failed });
    setSelectedRowKeys([]);
    loadInvoices();
  };

  const handleSendEmail = async () => {
    if (!emailModalInvoice) return;
    let values: any;
    try {
      values = await emailForm.validateFields();
    } catch {
      return;
    }

    const parseList = (v: string | undefined) =>
      (v || '')
        .split(/[,;\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);

    const toList = parseList(values.to);
    const ccList = parseList(values.cc);

    if (toList.length === 0) {
      message.error(t('Please enter at least one recipient email'));
      return;
    }

    setEmailSending(true);
    const invoice = emailModalInvoice;
    try {
      const encoded = emailAttachments.length ? await encodeAttachments(emailAttachments) : undefined;
      await invoiceService.sendInvoiceEmail(invoice.id, {
        to: toList,
        cc: ccList.length ? ccList : undefined,
        subject: values.subject,
        body: values.body,
        attachments: encoded,
      });
      message.success(
        `Invoice emailed to ${toList.join(', ')}` +
          (encoded?.length ? ` with ${encoded.length} extra attachment${encoded.length > 1 ? 's' : ''}` : '')
      );

      // Move the invoice to SENT after a successful email.
      //   - DRAFT (or stale CONFIRM with no journalEntryId): post the JE while flipping to SENT
      //   - CONFIRM with a JE already attached: just flip status, do NOT double-post
      //   - SENT / OVERDUE / PAID: leave the status alone (this was a resend)
      const needsPost =
        invoice.status === 'DRAFT' ||
        (invoice.status === 'CONFIRM' && !invoice.journalEntryId);

      if (needsPost) {
        try {
          const { arAccount, revenueAccount } = resolveAccountIds();
          const salesJournal = findSalesJournal(journals);
          if (arAccount && revenueAccount && salesJournal) {
            await invoiceService.postInvoice(invoice.id, {
              accountsReceivableAccountId: arAccount.id,
              revenueAccountId: revenueAccount.id,
              journalId: salesJournal.id,
            });
          } else {
            message.warning(
              t('Email sent, but invoice could not be posted (missing AR / Revenue account or Sales journal). Please post it manually.')
            );
          }
        } catch (postErr: any) {
          message.warning(
            t('Email sent, but posting the invoice failed: {{postErr}}', { postErr: postErr?.message || 'unknown error' })
          );
        }
      } else if (invoice.status === 'CONFIRM') {
        // JE already exists from Confirm; just promote to SENT.
        try {
          await invoiceService.updateInvoice(invoice.id, { status: 'SENT' });
        } catch (updErr: any) {
          message.warning(
            t('Email sent, but moving invoice to SENT failed: {{updErr}}', { updErr: updErr?.message || 'unknown error' })
          );
        }
      }

      closeSendEmailModal();
      loadInvoices();
      loadStats();
    } catch (error: any) {
      message.error(t('Failed to send invoice email: {{error}}', { error: error?.message || 'unknown error' }));
    } finally {
      setEmailSending(false);
    }
  };

  const handleCancelInvoice = async (invoice: Invoice) => {
    Modal.confirm({
      title: t('Cancel Invoice?'),
      content: t('Are you sure you want to cancel invoice {{invoiceNo}}? {{detail}}', {
        invoiceNo: invoice.invoiceNo,
        detail: invoice.status === 'SENT' || invoice.status === 'OVERDUE'
          ? t('This will create a reversing journal entry to undo the accounting impact.')
          : t('This will change the invoice status to CANCELLED.'),
      }),
      okText: t('Yes, Cancel Invoice'),
      okButtonProps: { danger: true },
      cancelText: t('No'),
      onOk: async () => {
        try {
          // Prefer Default Accounts from settings, fall back to code heuristic
          const { arAccount, revenueAccount } = resolveAccountIds();
          const salesJournal = findSalesJournal(journals);

          if (!arAccount || !revenueAccount || !salesJournal) {
            console.error('Cancel Invoice - Missing accounts/journals:', {
              arAccount,
              revenueAccount,
              salesJournal,
              allAccounts: accounts.map(a => ({ id: a.id, code: a.code, name: a.name })),
              allJournals: journals.map(j => ({ id: j.id, code: j.code, name: j.name }))
            });
            message.error(t('Missing required accounts or journal for cancellation. AR: {{arAccount}}, Revenue: {{revenueAccount}}, Journal: {{salesJournal}}', { arAccount: arAccount ? '✓' : '✗', revenueAccount: revenueAccount ? '✓' : '✗', salesJournal: salesJournal ? '✓' : '✗' }));
            return;
          }

          await invoiceService.cancelInvoice(invoice.id, {
            accountsReceivableAccountId: arAccount.id,
            revenueAccountId: revenueAccount.id,
            journalId: salesJournal.id
          });

          message.success(t('Invoice cancelled successfully'));
          loadInvoices();
          loadStats();
        } catch (error: any) {
          message.error(t('Failed to cancel invoice: {{message}}', { message: error.message }));
        }
      }
    });
  };

  const handleExport = async () => {
    if (invoices.length === 0) {
      message.warning(t('No data to export'));
      return;
    }
    const hide = message.loading(t('Building export...'), 0);
    try {
      // Build from the data already on screen.
      //
      // This used to fetch every invoice individually — Promise.all over the
      // whole list, thousands of requests fired at once. nginx rate-limits /api
      // (10r/s, burst 50, nodelay), so most were rejected with 503; a
      // `.catch(() => inv)` swallowed each rejection and fell back to the list
      // object, which carries accountId but no joined account. The export then
      // wrote an empty Account cell — silently, whole invoices at a time.
      // Reported by the client as "Account data唔齊全".
      //
      // The list response already contains every field the sheets need, and the
      // page already holds the full chart of accounts, so the account can be
      // resolved from accountId locally. No per-invoice requests at all.
      const fullInvoices = invoices;
      const accountById = new Map<string, any>(accounts.map((a: any) => [a.id, a]));

      // Departments: lines saved before the Department field was unlinked from HR
      // hold an id, newer ones hold the typed name. One lookup for the whole
      // export — never one per row.
      const deptResp: any = await apiService.get<any>('/hr/departments').catch(() => null);
      const deptList: any[] = Array.isArray(deptResp?.data) ? deptResp.data : (deptResp?.data?.departments || []);
      const departmentById = new Map<string, any>(deptList.map((d: any) => [d.id, d]));
      const departmentLabel = (item: any): string => {
        if (item.departmentName) return item.departmentName;
        const d = item.departmentId ? departmentById.get(item.departmentId) : null;
        return d ? `${d.code} - ${d.name}` : '';
      };
      const accountLabel = (item: any): string => {
        const acc = item.account || (item.accountId ? accountById.get(item.accountId) : null);
        return acc ? `${acc.code} - ${acc.name}` : '';
      };

      // The invoice total is in its own currency. HKD Amount is that converted at
      // the rate stored on the invoice — the same multiplication the ledger uses
      // (verified against posted USD invoices: totalAmount x exchangeRate equals
      // the journal entry's debits).
      //
      // An HKD invoice carries no rate and needs no conversion. A foreign invoice
      // with no rate is left BLANK rather than guessed at: 3 USD invoices on
      // production have none, and inventing a rate on an accounting export would
      // be worse than an empty cell.
      const hkdAmount = (inv: any): number | string => {
        const fcy = Number(inv.totalAmount || 0);
        if ((inv.currency || 'HKD') === 'HKD') return fcy;
        const rate = Number(inv.exchangeRate || 0);
        if (!rate) return '';
        return Math.round(fcy * rate * 100) / 100;
      };

      const wb = XLSX.utils.book_new();

      // Sheet 1: Invoice Summary
      const summaryRows = fullInvoices.map(inv => ({
        'Invoice No': inv.invoiceNo || '',
        'Customer': inv.customer?.name || '',
        'Status': (inv.status || '').toUpperCase(),
        'Invoice Date': inv.invoiceDate ? dayjs(inv.invoiceDate).format('YYYY-MM-DD') : '',
        'Due Date': inv.dueDate ? dayjs(inv.dueDate).format('YYYY-MM-DD') : '',
        'Currency': inv.currency || '',
        'FCY Amount': Number(inv.totalAmount || 0),
        'HKD Amount': hkdAmount(inv),
        'Payment Terms': inv.paymentTerms || '',
        'Contact Person': inv.contactPerson || '',
        'Contact Email': inv.contactEmail || '',
        'Bill Reference': inv.billReference || '',
      }));
      const ws1 = XLSX.utils.json_to_sheet(summaryRows);
      ws1['!cols'] = [
        { wch: 18 }, { wch: 32 }, { wch: 12 }, { wch: 12 },
        { wch: 12 }, { wch: 8 }, { wch: 14 }, { wch: 14 }, { wch: 16 },
        { wch: 18 }, { wch: 24 }, { wch: 16 },
      ];
      XLSX.utils.book_append_sheet(wb, ws1, 'Invoice Summary');

      // Sheet 2: Line Items
      const lineRows: Record<string, any>[] = [];
      fullInvoices.forEach(inv => {
        if (inv.items && inv.items.length > 0) {
          inv.items.forEach((item: any) => {
            lineRows.push({
              'Invoice No': inv.invoiceNo || '',
              'Customer': inv.customer?.name || '',
              'Item Code': item.itemCode || item.product?.itemCode || '',
              'Description': item.description || item.product?.itemName || '',
              'Account': accountLabel(item),
              'Department': departmentLabel(item),
              'Quantity': Number(item.quantity || 0),
              'Unit': item.unit || '',
              'Unit Price': Number(item.unitPrice || 0),
              'Discount': item.discount ? `${item.discount}${item.discountType === 'percentage' ? '%' : ''}` : '',
              'Tax Rate': item.taxRate ? `${item.taxRate}%` : '',
              'Line Total': Number(item.lineTotal || 0),
            });
          });
        }
      });
      const ws2 = XLSX.utils.json_to_sheet(lineRows.length > 0 ? lineRows : [{ 'Note': 'No line items found' }]);
      ws2['!cols'] = [
        { wch: 18 }, { wch: 32 }, { wch: 16 }, { wch: 36 },
        { wch: 24 }, { wch: 18 }, { wch: 10 }, { wch: 10 }, { wch: 12 },
        { wch: 10 }, { wch: 10 }, { wch: 14 },
      ];
      XLSX.utils.book_append_sheet(wb, ws2, 'Line Items');

      XLSX.writeFile(wb, `Invoices_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
      hide();
      message.success(t('Exported {{length}} invoices with line items', { length: fullInvoices.length }));

      // A blank Account now means the invoice line genuinely has no account
      // saved, not that the export lost it. Say so rather than handing over a
      // spreadsheet with quiet holes in it — that silence is what let the old
      // bug run unnoticed.
      const missingAccount = lineRows.filter(r => !r['Account']).length;
      if (missingAccount > 0) {
        message.warning(
          t('{{count}} line item(s) have no account set on the invoice itself — set one on those invoices to fill the Account column.', { count: missingAccount }),
          8
        );
      }
    } catch (error: any) {
      hide();
      message.error(t('Export failed: {{message}}', { message: error.message }));
    }
  };

  const handleDownloadPDF = async (invoice: Invoice) => {
    try {
      // Get the auth token
      const token = localStorage.getItem('authToken');
      if (!token) {
        message.error(t('No authentication token found. Please log in again.'));
        return;
      }

      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';

      // Fetch PDF with authentication
      const response = await fetch(`${apiBaseUrl}/pdf/invoice/${invoice.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('PDF download failed:', response.status, errorText);
        throw new Error(`Failed to download PDF (${response.status})`);
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoice.invoiceNo}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success(t('PDF downloaded successfully'));
    } catch (error: any) {
      message.error(t('Failed to download PDF: {{message}}', { message: error.message }));
    }
  };

  const handleBulkDownloadPDF = async () => {
    const selected = invoices.filter(inv => selectedRowKeys.includes(inv.id));
    if (selected.length === 0) {
      message.warning(t('Please select invoices to download'));
      return;
    }

    setBulkDownloading(true);
    const token = localStorage.getItem('authToken');
    if (!token) {
      message.error(t('No authentication token found. Please log in again.'));
      setBulkDownloading(false);
      return;
    }

    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';
      let successCount = 0;
      let failCount = 0;
      const usedNames = new Set<string>();

      for (const invoice of selected) {
        try {
          const response = await fetch(`${apiBaseUrl}/pdf/invoice/${invoice.id}/download`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const arrayBuffer = await response.arrayBuffer();
          let fileName = `${invoice.invoiceNo || invoice.id}.pdf`;
          // Ensure unique file names in zip
          if (usedNames.has(fileName)) {
            fileName = `${invoice.invoiceNo || invoice.id}_${invoice.id.slice(-6)}.pdf`;
          }
          usedNames.add(fileName);
          zip.file(fileName, arrayBuffer);
          successCount++;
        } catch (err) {
          console.error(`Failed to fetch PDF for ${invoice.invoiceNo}:`, err);
          failCount++;
        }
      }

      if (successCount > 0) {
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const url = window.URL.createObjectURL(zipBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `invoices_${dayjs().format('YYYYMMDD_HHmmss')}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

      if (failCount === 0) {
        message.success(t('Downloaded {{successCount}} invoice PDF(s) as ZIP', { successCount }));
      } else {
        message.warning(t('Downloaded {{successCount}}, failed {{failCount}}', { successCount, failCount }));
      }
      setSelectedRowKeys([]);
    } catch (error: any) {
      message.error(t('Bulk download failed: {{message}}', { message: error.message }));
    } finally {
      setBulkDownloading(false);
    }
  };

  // The receipt is the same document with the title swapped and the payment
  // date added — ?mode=receipt on the same endpoint. Only offered once the
  // invoice is PAID, so there is always a payment date to print.
  const handlePrintReceipt = (invoice: Invoice) => handlePrintInvoice(invoice, true);

  const handlePrintInvoice = async (invoice: Invoice, asReceipt = false) => {
    try {
      // Get the auth token
      const token = localStorage.getItem('authToken');
      if (!token) {
        message.error(t('No authentication token found. Please log in again.'));
        return;
      }

      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';

      // Fetch HTML with authentication
      const response = await fetch(`${apiBaseUrl}/pdf/invoice/${invoice.id}${asReceipt ? '?mode=receipt' : ''}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Print invoice failed:', response.status, errorText);
        throw new Error(`Failed to load invoice for printing (${response.status})`);
      }

      // Get HTML content and open in new window
      const html = await response.text();
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(html);
        printWindow.document.close();
        // Trigger print dialog after content loads
        printWindow.onload = () => {
          printWindow.print();
        };
      } else {
        message.error(t('Failed to open print window. Please allow popups for this site.'));
      }
    } catch (error: any) {
      message.error(t('Failed to print invoice: {{message}}', { message: error.message }));
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      DRAFT: 'default',
      CONFIRM: 'cyan',
      SENT: 'processing',
      PAID: 'success',
      OVERDUE: 'error',
      CANCELLED: 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status: string) => {
    const icons: Record<string, React.ReactNode> = {
      DRAFT: <FileTextOutlined />,
      CONFIRM: <SaveOutlined />,
      SENT: <ClockCircleOutlined />,
      PAID: <CheckCircleOutlined />,
      OVERDUE: <ExclamationCircleOutlined />,
      CANCELLED: <CloseCircleOutlined />
    };
    return icons[status] || null;
  };

  // Sorters: group-header rows always stay where they are (their parent group
  // controls position); only the leaf invoice rows actually re-sort. AntD calls
  // the sorter for every pair, so guarding both sides keeps headers stable.
  const safeSort = <T,>(getter: (row: any) => T, compare: (a: T, b: T) => number) =>
    (a: any, b: any) => {
      if (a.isGroupHeader || b.isGroupHeader) return 0;
      return compare(getter(a), getter(b));
    };
  const strCmp = (a: string, b: string) => (a || '').localeCompare(b || '');
  const numCmp = (a: number, b: number) => (a || 0) - (b || 0);
  const dateCmp = (a: any, b: any) => {
    const ta = a ? new Date(a).getTime() : Number.POSITIVE_INFINITY;
    const tb = b ? new Date(b).getTime() : Number.POSITIVE_INFINITY;
    return ta - tb;
  };

  const columns: ColumnsType<any> = [
    {
      title: t('Invoice No'),
      dataIndex: 'invoiceNo',
      key: 'invoiceNo',
      width: 130,
      fixed: 'left',
      sorter: safeSort(r => r.invoiceNo, strCmp),
      render: (text, record) => {
        if (record.isGroupHeader) {
          return {
            children: (
              <div style={{
                fontWeight: 'bold',
                fontSize: '14px',
                color: '#1890ff',
                padding: '8px 0'
              }}>
                📁 {record.groupName} ({record.groupItems.length} items)
              </div>
            ),
            props: {
              colSpan: 8 // span the 7 visible columns + actions
            }
          };
        }
        return <strong>{text}</strong>;
      }
    },
    {
      title: t('Customer'),
      dataIndex: ['customer', 'name'],
      key: 'customer',
      width: 180,
      sorter: safeSort(r => r.customer?.name || r.customerId || '', strCmp),
      render: (text, record) => {
        if (record.isGroupHeader) return { props: { colSpan: 0 } };
        return text || record.customerId;
      }
    },
    {
      title: t('Bill Ref.'),
      dataIndex: 'billReference',
      key: 'billReference',
      width: 140,
      sorter: safeSort(r => r.billReference || '', strCmp),
      render: (ref, record) => {
        if (record.isGroupHeader) return { props: { colSpan: 0 } };
        return ref ? <span style={{ color: '#666' }}>{ref}</span> : <span style={{ color: '#ccc' }}>—</span>;
      }
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      sorter: safeSort(r => r.status || '', strCmp),
      render: (status, record) => {
        if (record.isGroupHeader) return { props: { colSpan: 0 } };
        return (
          <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
            {status}
          </Tag>
        );
      }
    },
    {
      title: t('Amount'),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 130,
      align: 'right',
      sorter: safeSort(r => Number(r.totalAmount), numCmp),
      render: (amount, record) => {
        if (record.isGroupHeader) return { props: { colSpan: 0 } };
        const currency = record.currency || 'HKD';
        return (
          <span style={{ fontWeight: 500 }}>
            {currency} {Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        );
      }
    },
    {
      title: t('Due Date'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 120,
      sorter: safeSort(r => r.dueDate, dateCmp),
      render: (date, record) => {
        if (record.isGroupHeader) return { props: { colSpan: 0 } };
        return date ? dayjs(date).format('YYYY-MM-DD') : '-';
      }
    },
    {
      title: t('Invoice Date'),
      dataIndex: 'invoiceDate',
      key: 'invoiceDate',
      width: 120,
      sorter: safeSort(r => r.invoiceDate, dateCmp),
      defaultSortOrder: 'descend',
      render: (date, record) => {
        if (record.isGroupHeader) return { props: { colSpan: 0 } };
        return date ? dayjs(date).format('YYYY-MM-DD') : '-';
      }
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 460,
      fixed: 'right',
      render: (_, record) => {
        if (record.isGroupHeader) return { props: { colSpan: 0 } };
        return (
        <Space size="small" wrap>
          <Button
            type="default"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => onView?.(record)}
          >
            {t('View')}
                              </Button>
          <Button
            type="default"
            size="small"
            icon={<EditOutlined />}
            onClick={() => onEdit?.(record)}
          >
            {t('Edit')}
                              </Button>
          <Button
            type="default"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => onDuplicate?.(record)}
          >
            {t('Duplicate')}
                              </Button>
          {record.status === 'DRAFT' && (
            <>
              <Button
                size="small"
                icon={<SaveOutlined />}
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: 'white' }}
                onClick={() => handleStatusChange(record, 'CONFIRM')}
              >
                {t('Confirm')}
                                          </Button>
              <Button
                type="primary"
                size="small"
                icon={<SendOutlined />}
                onClick={() => openSendEmailModal(record)}
              >
                {t('Send')}
                                          </Button>
            </>
          )}
          {record.status === 'CONFIRM' && (
            <Button
              type="primary"
              size="small"
              icon={<SendOutlined />}
              onClick={() => openSendEmailModal(record)}
            >
              {t('Send')}
                                    </Button>
          )}
          {/* Payable once the invoice is on the ledger. CONFIRM already posted the
              journal entry (Confirm is what creates it) — Send only emails the
              customer — so a confirmed invoice can legitimately be paid without
              ever being sent. OVERDUE is likewise just an unpaid SENT invoice;
              the payment form's allocation list already treated it as payable. */}
          {(record.status === 'SENT' || record.status === 'CONFIRM' || record.status === 'OVERDUE') && (
            <Button
              type="default"
              size="small"
              icon={<DollarOutlined />}
              style={{ color: '#52c41a' }}
              onClick={() => onMarkPaid?.(record)}
            >
              {t('Mark Paid')}
                                    </Button>
          )}
          {(record.status === 'SENT' || record.status === 'OVERDUE' || record.status === 'PAID') && (
            <Button
              type="default"
              size="small"
              icon={<SendOutlined />}
              onClick={() => openSendEmailModal(record)}
            >
              {t('Email')}
                                    </Button>
          )}
          {(record.status === 'DRAFT' || record.status === 'CONFIRM' || record.status === 'CANCELLED') && (
            <Popconfirm
              title={t('Are you sure to delete this invoice?')}
              /*
                Deleting takes the invoice's journal entries with it. For an
                invoice from an earlier month that rewrites a period which has
                very likely already been reported, so say so here rather than
                letting it happen quietly — the client asked to keep the
                ability, on the understanding that they know what it costs.
              */
              description={
                !dayjs(record.invoiceDate || record.createdAt).isSame(dayjs(), 'month')
                  ? t('This invoice is from {{month}}. Deleting it also removes its journal entries, which will change that month\'s figures.',
                      { month: dayjs(record.invoiceDate || record.createdAt).format('MMMM YYYY') })
                  : t('Its journal entries will be deleted with it.')
              }
              onConfirm={() => handleDelete(record.id)}
              okText={t('Yes')}
              cancelText={t('No')}
            >
              <Button
                type="default"
                size="small"
                danger
                icon={<DeleteOutlined />}
              >
                {t('Delete')}
                                          </Button>
            </Popconfirm>
          )}
          {/* PDF button removed — Print covers the same flow. handleDownloadPDF
              + DownloadOutlined import are still used by the row Print button
              and the bulk-export toolbar above the table. */}
          <Button
            type="default"
            size="small"
            icon={<PrinterOutlined />}
            onClick={() => handlePrintInvoice(record)}
          >
            {t('Print')}
                              </Button>
          {/* Receipt — same document, titled RECEIPT and carrying the payment
              date. Only once the invoice is actually paid. */}
          {record.status === 'PAID' && (
            <Button
              type="default"
              size="small"
              icon={<PrinterOutlined />}
              onClick={() => handlePrintReceipt(record)}
            >
              {t('Receipt')}
            </Button>
          )}
          {/* Cancel button for all invoices except PAID and already CANCELLED */}
          {record.status !== 'PAID' && record.status !== 'CANCELLED' && (
            <Button
              type="default"
              size="small"
              danger
              icon={<CloseCircleOutlined />}
              onClick={() => handleCancelInvoice(record)}
            >
              {t('Cancel')}
                                    </Button>
          )}
        </Space>
        );
      }
    }
  ];

  return (
    <div>
      {/* Statistics Cards */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title={t('Total Invoices')}
                value={stats.totalInvoices}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title={t('Total Amount')}
                value={stats.totalAmount}
                prefix="$"
                precision={2}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title={t('Paid Amount')}
                value={stats.paidAmount}
                prefix="$"
                precision={2}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title={t('Outstanding')}
                value={stats.outstandingAmount}
                prefix="$"
                precision={2}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters and Actions */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space size="middle" wrap>
              <Input.Search
                placeholder={t('Search invoice no. or customer')}
                style={{ width: 280 }}
                allowClear
                value={searchValue}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchValue(value);
                  // Debounce search
                  if (searchTimeoutRef.current) {
                    clearTimeout(searchTimeoutRef.current);
                  }
                  searchTimeoutRef.current = setTimeout(() => {
                    setFilters({ ...filters, search: value || undefined });
                  }, 500);
                }}
                onSearch={(value) => {
                  if (searchTimeoutRef.current) {
                    clearTimeout(searchTimeoutRef.current);
                  }
                  setFilters({ ...filters, search: value || undefined });
                }}
              />
              {/*
                Two filters, not one, because an invoice has two independent
                states: where the document is, and whether the money arrived.
                They combine with AND, so "confirmed AND still unpaid" is one
                query. Selecting Confirm also returns confirmed invoices that
                have since been paid — the backend looks under a stored PAID at
                the status it replaced.
              */}
              <Select
                mode="multiple"
                placeholder={t('Invoice Status')}
                style={{ minWidth: 200 }}
                allowClear
                maxTagCount="responsive"
                value={filters.status}
                onChange={(value) => setFilters({ ...filters, status: value })}
              >
                <Option value="DRAFT">{t('Draft')}</Option>
                <Option value="CONFIRM">{t('Confirm')}</Option>
                <Option value="SENT">{t('Sent')}</Option>
                <Option value="CANCELLED">{t('Cancelled')}</Option>
              </Select>
              <Select
                mode="multiple"
                placeholder={t('Payment')}
                style={{ minWidth: 170 }}
                allowClear
                maxTagCount="responsive"
                value={filters.payment}
                onChange={(value) => setFilters({ ...filters, payment: value })}
              >
                <Option value="PAID">{t('Paid')}</Option>
                <Option value="UNPAID">{t('Unpaid')}</Option>
                <Option value="OVERDUE">{t('Overdue')}</Option>
              </Select>
              <RangePicker
                placeholder={['Invoice Date From', 'Invoice Date To']}
                onChange={(dates) => {
                  if (dates) {
                    setFilters({
                      ...filters,
                      dateFrom: dates[0]?.format('YYYY-MM-DD'),
                      dateTo: dates[1]?.format('YYYY-MM-DD')
                    });
                  } else {
                    setFilters({ ...filters, dateFrom: undefined, dateTo: undefined });
                  }
                }}
              />
              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'status',
                      label: t('Status'),
                      icon: <GroupOutlined />,
                      onClick: () => setGroupBy('status')
                    },
                    {
                      key: 'customer',
                      label: t('Customer'),
                      icon: <GroupOutlined />,
                      onClick: () => setGroupBy('customer')
                    },
                    {
                      key: 'dueDate',
                      label: t('Due Date'),
                      icon: <GroupOutlined />,
                      onClick: () => setGroupBy('dueDate')
                    },
                    {
                      key: 'month',
                      label: t('Invoice Month'),
                      icon: <GroupOutlined />,
                      onClick: () => setGroupBy('month')
                    },
                    {
                      type: 'divider'
                    },
                    {
                      key: 'none',
                      label: t('No Grouping'),
                      onClick: () => setGroupBy(null)
                    }
                  ]
                }}
              >
                <Button icon={<GroupOutlined />}>
                  {t('Group By')} {groupBy ? `: ${groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}` : ''} <DownOutlined />
                </Button>
              </Dropdown>
            </Space>
          </Col>
          <Col>
            <Space>
              {selectedRowKeys.length > 0 && (
                <Popconfirm
                  title={`Email ${selectedRowKeys.length} invoice${selectedRowKeys.length > 1 ? 's' : ''}?`}
                  description={t('One email per invoice, each to its own customer. Any without an email address are skipped and listed.')}
                  okText={t('Send')}
                  cancelText={t('Cancel')}
                  onConfirm={handleBulkSendEmail}
                >
                  <Button type="primary" icon={<MailOutlined />} loading={bulkSending}>
                    {t('Email')} {selectedRowKeys.length} invoice{selectedRowKeys.length > 1 ? 's' : ''}
                  </Button>
                </Popconfirm>
              )}
              {selectedRowKeys.length > 0 && (
                <Button
                  icon={<DownloadOutlined />}
                  loading={bulkDownloading}
                  onClick={handleBulkDownloadPDF}
                >
                  {t('Download')} {selectedRowKeys.length} PDF{selectedRowKeys.length > 1 ? 's' : ''}
                </Button>
              )}
              <Button icon={<UploadOutlined />} onClick={() => setImportModalVisible(true)}>
                {t('Import')}
                                            </Button>
              <Button icon={<DownloadOutlined />} onClick={handleExport}>
                {t('Export')}
                                            </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={onCreate}
              >
                {t('Create Invoice')}
                                            </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Invoices Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={processedInvoices}
          rowKey="id"
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
            getCheckboxProps: (record: any) => ({
              disabled: record.isGroupHeader,
            }),
          }}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => t('Total {{total}} invoices', { total })
          }}
          scroll={{ x: 1200 }}
          rowClassName={(record) => record.isGroupHeader ? 'group-header-row' : ''}
        />
      </Card>

      <style>{`
        .group-header-row {
          background-color: #f0f5ff !important;
          font-weight: 600;
        }
        .group-header-row:hover {
          background-color: #e6f0ff !important;
        }
        .group-header-row td {
          border-bottom: 2px solid #d9d9d9 !important;
        }
      `}</style>

      <InvoiceBulkUploadModal
        visible={importModalVisible}
        onClose={() => setImportModalVisible(false)}
        onSuccess={() => { loadInvoices(); loadStats(); }}
        customers={customers}
        accounts={accounts}
        companyId=""
      />


      {/* What actually happened in a bulk send — nothing is left silent. */}
      <Modal
        title={t('Bulk email results')}
        open={!!bulkResult}
        onCancel={() => setBulkResult(null)}
        footer={[
          <Button key="close" type="primary" onClick={() => setBulkResult(null)}>{t('Close')}</Button>
        ]}
        width={640}
      >
        {bulkResult && (
          <>
            {bulkResult.sent.length > 0 && (
              <Alert
                type="success"
                showIcon
                style={{ marginBottom: 12 }}
                message={`Sent ${bulkResult.sent.length} invoice${bulkResult.sent.length > 1 ? 's' : ''}`}
              />
            )}
            {bulkResult.skipped.length > 0 && (
              <>
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message={`Skipped ${bulkResult.skipped.length} — no email address on file`}
                />
                <Table
                  size="small"
                  pagination={false}
                  rowKey={(r: any) => r.invoiceNo}
                  dataSource={bulkResult.skipped}
                  style={{ marginBottom: 12 }}
                  columns={[
                    { title: t('Invoice'), dataIndex: 'invoiceNo', width: 140 },
                    { title: t('Customer'), dataIndex: 'customer' }
                  ]}
                />
              </>
            )}
            {bulkResult.failed.length > 0 && (
              <>
                <Alert
                  type="error"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message={`${bulkResult.failed.length} could not be sent`}
                />
                <Table
                  size="small"
                  pagination={false}
                  rowKey={(r: any) => r.invoiceNo}
                  dataSource={bulkResult.failed}
                  columns={[
                    { title: t('Invoice'), dataIndex: 'invoiceNo', width: 140 },
                    { title: t('Reason'), dataIndex: 'error' }
                  ]}
                />
              </>
            )}
          </>
        )}
      </Modal>

      <Modal
        title={
          emailModalInvoice
            ? `Send Invoice ${emailModalInvoice.invoiceNo} by Email`
            : t('Send Invoice by Email')
        }
        open={!!emailModalInvoice}
        onCancel={emailSending ? undefined : closeSendEmailModal}
        onOk={handleSendEmail}
        okText={t('Send Email')}
        okButtonProps={{ icon: <SendOutlined />, loading: emailSending }}
        cancelButtonProps={{ disabled: emailSending }}
        width={640}
        destroyOnClose
      >
        {emailModalInvoice && (
          <>
            {emailModalInvoice.status === 'DRAFT' && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={t('This invoice has not been confirmed yet. Sending will also post the journal entry and move it to SENT.')}
              />
            )}
            {emailModalInvoice.status === 'CONFIRM' && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={t('Sending will move the invoice to SENT. The journal entry is already posted from the Confirm step.')}
              />
            )}
            <Form form={emailForm} layout="vertical" preserve={false}>
              <Form.Item
                label={t('To')}
                name="to"
                rules={[{ required: true, message: t('Please enter at least one recipient email') }]}
              >
                <Input placeholder="customer@example.com (comma-separated for multiple)" />
              </Form.Item>
              <Form.Item label="CC" name="cc">
                <Input placeholder="optional, comma-separated" />
              </Form.Item>
              <Form.Item
                label={t('Subject')}
                name="subject"
                rules={[{ required: true, message: t('Please enter a subject') }]}
              >
                <Input />
              </Form.Item>
              <Form.Item label={t('Message')} name="body">
                <Input.TextArea autoSize={{ minRows: 6, maxRows: 14 }} />
              </Form.Item>
              <Form.Item label={t('Attachments')} style={{ marginBottom: 8 }}>
                <Upload
                  multiple
                  // Hold the files in the browser and send them with the message;
                  // there is nowhere to upload them to on their own.
                  beforeUpload={(file) => {
                    setEmailAttachments(prev => [...prev, file]);
                    return false;
                  }}
                  onRemove={(file) => {
                    setEmailAttachments(prev => prev.filter(f => f.uid !== (file as any).uid));
                  }}
                  fileList={emailAttachments as any}
                >
                  <Button icon={<UploadOutlined />}>{t('Add a file')}</Button>
                </Upload>
              </Form.Item>
              <div style={{ color: '#888', fontSize: 12 }}>
                {t('The invoice PDF is attached automatically. Anything added above is sent with it (15 MB in total).')}
                                            </div>
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
};

export default InvoiceList;